import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, DataTable, StatusBadge, EmptyState, ConfirmDialog } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, RotateCcw, Truck, Eye, Ban, Copy, Check, Search, RefreshCw } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { can } from "@/lib/permissions";

// Reverse-lifecycle tabs — match return_service.RETURN_TAB_STATUS_MAP. `null` = no filter.
const TABS = [
  { id: "requested", label: "Requested", group: "requested" },
  { id: "ready", label: "Ready to Ship", group: "ready" },
  { id: "pickup", label: "Pickup", group: "pickup" },
  { id: "transit", label: "In Transit", group: "transit" },
  { id: "delivered", label: "Returned", group: "delivered" },
  { id: "cancelled", label: "Cancelled", group: "cancelled" },
  { id: "all", label: "All", group: null },
];

// Humanised reason labels (mirror models/b2c_return.RETURN_REASONS).
const REASON_LABELS = {
  damaged: "Damaged / defective",
  wrong_item: "Wrong item",
  not_as_described: "Not as described",
  size_fit: "Size / fit issue",
  quality: "Quality issue",
  missing_parts: "Missing parts",
  no_longer_needed: "No longer needed",
  other: "Other",
};

const PAGE_SIZE = 50;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const CopyableAwb = ({ awb }) => {
  const [copied, setCopied] = useState(false);
  if (!awb) return <span className="text-sm text-slate-400">-</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(awb).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 text-sm font-mono text-slate-700 hover:text-brand"
      title="Copy reverse AWB"
    >
      {awb}
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
    </button>
  );
};

const TypeBadge = ({ type }) =>
  type === "exchange" ? (
    <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">
      <RefreshCw className="w-3 h-3 mr-1" /> Exchange
    </Badge>
  ) : (
    <Badge variant="outline" className="border-slate-200 text-slate-700">
      <RotateCcw className="w-3 h-3 mr-1" /> Return
    </Badge>
  );

const B2CReturns = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useLocalStorageState("b2c-returns:tab", "all");
  const [search, setSearch] = useLocalStorageState("b2c-returns:search", "");
  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const abortRef = useRef(null);

  useEffect(() => {
    const grp = searchParams.get("status_group") || searchParams.get("tab");
    if (grp && TABS.some((t) => t.id === grp)) setActiveTab(grp);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  const tabGroup = (TABS.find((t) => t.id === activeTab) || TABS[TABS.length - 1]).group;

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (tabGroup) params.status_group = tabGroup;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/seller/returns`, { params, signal: controller.signal });
      setData(res);
      if (res.counts) setCounts(res.counts);
      setNotEnabled(false);
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      if (e?.response?.status === 422) {
        setNotEnabled(true);
        setData({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
      } else {
        toast.error(errDetail(e, "Failed to load returns"));
      }
    } finally {
      setLoading(false);
    }
  }, [page, tabGroup, search]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search]);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/b2c/seller/returns/${cancelTarget.id}/cancel`);
      toast.success("Return cancelled — any reverse-shipping charge is auto-refunded.");
      setCancelTarget(null);
      fetchReturns();
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel this return"));
    } finally {
      setCancelling(false);
    }
  };

  const canShip = can(user, "b2c.returns:ship");
  const canCancel = can(user, "b2c.returns:cancel");
  const canCreate = can(user, "b2c.returns:create");
  const isCancellable = (r) => ["REQUESTED", "AWB_ASSIGNED", "PICKUP_SCHEDULED"].includes(r.status);
  const tabCount = (id) => (id === "all" ? counts.all ?? data.total : counts[id]);

  const columns = [
    {
      key: "return",
      header: "Return",
      primary: true,
      cell: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/seller/b2c/returns/${r.id}`); }}
          className="text-sm font-medium text-brand hover:underline"
        >
          {r.return_number}
        </button>
      ),
    },
    {
      key: "date",
      header: "Date",
      hideOnMobile: true,
      cell: (r) => (
        <div className="whitespace-nowrap text-sm text-slate-600">
          <div>{formatToIST(r.created_at, false)}</div>
          <div className="text-xs text-slate-500">{formatToIST(r.created_at).split(", ")[1]}</div>
        </div>
      ),
    },
    { key: "type", header: "Type", cell: (r) => <TypeBadge type={r.type} /> },
    {
      key: "order",
      header: "Order",
      hideOnMobile: true,
      cell: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/seller/b2c/orders/${r.order_id}`); }}
          className="text-sm text-slate-600 hover:text-brand hover:underline"
        >
          {r.order_number || "-"}
        </button>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      cell: (r) => (
        <div className="text-sm">
          <div className="font-medium text-slate-800">{r.buyer?.name || "-"}</div>
          <div className="text-xs text-slate-500">{[r.buyer?.city, r.buyer?.pincode].filter(Boolean).join(", ")}</div>
        </div>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      hideOnMobile: true,
      cell: (r) => <span className="text-sm text-slate-700">{REASON_LABELS[r.reason] || r.reason || "-"}</span>,
    },
    { key: "courier", header: "Courier", cell: (r) => <span className="text-sm text-slate-700">{r.courier_code ? (r.courier_name || r.courier_code) : <span className="text-slate-400">-</span>}</span> },
    { key: "awb", header: "Reverse AWB", cell: (r) => <CopyableAwb awb={r.awb} /> },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} kind="b2c" /> },
    {
      key: "actions",
      header: "Actions",
      isActions: true,
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {r.status === "REQUESTED" && canShip && (
            <Button size="sm" onClick={() => navigate(`/seller/b2c/returns/${r.id}`)}>
              <Truck className="w-3.5 h-3.5 mr-1" /> Ship
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/seller/b2c/returns/${r.id}`)}>
            <Eye className="w-3.5 h-3.5 mr-1" /> View
          </Button>
          {isCancellable(r) && canCancel && (
            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
              onClick={() => setCancelTarget(r)}>
              <Ban className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const emptyNode = (
    <EmptyState
      icon={RotateCcw}
      title={search ? "No matching returns" : "No returns yet"}
      description={
        search
          ? "Try a different return / order number, AWB or phone."
          : "Raise a return or exchange against a delivered order to pick the parcel back up from your buyer."
      }
      action={
        !search && canCreate ? (
          <Button onClick={() => navigate("/seller/b2c/returns/create")}>
            <Plus className="w-4 h-4 mr-2" /> New Return / Exchange
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Returns & Exchange"
          subtitle="Pick parcels back up from your buyers — returns & exchanges"
          actions={
            canCreate ? (
              <Button onClick={() => navigate("/seller/b2c/returns/create")} data-testid="add-b2c-return-btn">
                <Plus className="w-4 h-4 mr-2" /> New Return / Exchange
              </Button>
            ) : undefined
          }
        />

        {notEnabled ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={RotateCcw}
                title="B2C is not enabled yet"
                description="Your account doesn't have a B2C plan configured. Ask your account manager to enable B2C shipping for your organisation."
              />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
              {TABS.map((tab) => {
                const c = tabCount(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? "border-brand text-brand"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    }`}
                  >
                    {tab.label}
                    {c != null && <span className="font-bold text-brand"> ({c})</span>}
                  </button>
                );
              })}
            </div>

            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by return #, order #, AWB or buyer phone..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardContent>
            </Card>

            <DataTable
              columns={columns}
              data={data.items}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/seller/b2c/returns/${r.id}`)}
              loading={loading}
              empty={emptyNode}
              pagination={
                totalPages > 1
                  ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage }
                  : undefined
              }
            />
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(v) => !v && setCancelTarget(null)}
        title={`Cancel return ${cancelTarget?.return_number || ""}?`}
        description="This cancels the reverse pickup before it's collected. If the reverse shipment was already booked, the reverse-shipping charge is automatically refunded to your wallet."
        confirmLabel="Cancel return"
        tone="destructive"
        loading={cancelling}
        onConfirm={doCancel}
      />
    </SellerLayout>
  );
};

export default B2CReturns;
